import React, { useMemo, useContext } from 'react';
import { Number } from '../../../common/common';
import { ThreeDots } from 'react-bootstrap-icons';
import { useTable, useSortBy, useExpanded } from 'react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faSort,
  faCaretUp,
} from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as CirclePlus } from '../../../../../assets/icons/circle-plus.svg';
import { ReactComponent as CircleMinus } from '../../../../../assets/icons/circle-minus.svg';
import AppContext from '../../../../context';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const ActivePosTableMob = (props) => {
  const context = useContext(AppContext);

  const get_data_class = (data) => {
    const color_class = data < 0 ? 'negative' : 'positive';
    return color_class;
  };

  // Add instrumentType to each row of data
  const updatedData = useMemo(() => {
    if (props.summary && props.summary.active) {
      return props.summary.active.map((item) => {
        // Add instrumentType to each sub_row
        const updatedSubRows = item.sub_rows
          ? item.sub_rows.map((subRow) => ({
              ...subRow,
              instrumentType: item.name.toLowerCase().replaceAll(' ', '_'), // Adding instrumentType to each sub_row
            }))
          : [];

        return {
          ...item,
          sub_rows: updatedSubRows, // Use the updated sub_rows with instrumentType
        };
      });
    }
    return [];
  }, [props.summary]);

  const columns = useMemo(() => {
    if (props.summary.active) {
      const config = [
        {
          id: 'expander',
          Header: 'Company',
          accessor: 'name',
          Cell: (props) => {
            return (
              <>
                {props.row.depth === 0 ? (
                  <>
                    <span>{props.value}</span>
                    {props.value === 'Mutual Funds' && props.row.isExpanded ? (
                      <span className="beta_pill">Beta</span>
                    ) : (
                      ''
                    )}
                    {props.row.canExpand && (
                      <span
                        {...props.row.getToggleRowExpandedProps()}
                        className="toggler"
                      >
                        {props.row.isExpanded ? (
                          <CircleMinus />
                        ) : (
                          <CirclePlus />
                        )}
                      </span>
                    )}
                  </>
                ) : props.row.original.slug &&
                  props.row.original.instrumentType == 'direct_equity' ? (
                  <a
                    href={`${window.location.origin}/company/${props.row.original.slug}`}
                    target="_blank"
                    className={`company-link ml-2`}
                  >
                    {props.value}{' '}
                  </a>
                ) : props.value.length >= 18 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{props.value}</Tooltip>}
                  >
                    <span className="ml-2">{props.value.slice(0, 18)}...</span>
                  </OverlayTrigger>
                ) : (
                  <span className="ml-2">{props.value}</span>
                )}
                <div
                  className={`weight ${props.row.depth !== 0 ? 'ml-2' : ''}`}
                >
                  {props.row.depth !== 0 && (
                    <>
                      <span className="text">QTY</span>
                      {props.row.original.shares ? (
                        <Number
                          value={props.row.original.shares}
                          show_decimals={true}
                          points="2"
                        />
                      ) : (
                        ''
                      )}
                    </>
                  )}
                  <span className="text ml-2">Weight</span>
                  {props.row.original.weight}%
                </div>
              </>
            );
          },
          thClass: 'th-name',
          tdClass: 'table-data name',
          disableSortBy: true,
        },
        {
          Header: '1D Chg',
          accessor: 'one_day_change',
          Cell: (props) => {
            if (
              props.row.original.sub_rows &&
              !props.row.original.sub_rows.length
            ) {
              return '-';
            }
            return (
              <>
                <div className="align-middle">
                  <Number
                    value={props.row.original.one_day_change}
                    format={true}
                  />
                </div>{' '}
                <span className="pill">
                  {props.row.original.one_day_change_perc}%
                </span>
              </>
            );
          },
          sortType: (rowA, rowB, id, desc) => {
            if (!rowA.depth) return false;
            let a = rowA.original['one_day_change_perc'];
            let b = rowB.original['one_day_change_perc'];
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
          },
          thClass: 'pl-5',
          tdClass: 'table-data',
        },
        {
          Header: 'P&L',
          accessor: 'gain_loss',
          Cell: (props) => {
            if (
              props.row.original.sub_rows &&
              !props.row.original.sub_rows.length
            ) {
              return '-';
            }
            return (
              <>
                <div className="align-middle">
                  <Number value={props.row.original.gain_loss} format={true} />
                </div>{' '}
                <span className="pill">
                  {props.returnsType === 'xirr'
                    ? props.row.original.gain_loss_perc
                    : props.row.original.gain_loss_abs_perc}
                  %
                </span>
              </>
            );
          },
          sortType: (rowA, rowB, id, desc) => {
            if (!rowA.depth) return false;
            let datum =
              props.returnsType === 'xirr'
                ? 'gain_loss_perc'
                : 'gain_loss_abs_perc';
            let a = rowA.original[datum] === '-' ? 0 : rowA.original[datum];
            let b = rowB.original[datum] === '-' ? 0 : rowB.original[datum];
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
          },
          thClass: `pl-5`,
          tdClass: `table-data`,
        },
        {
          Header: 'Current Amt',
          accessor: 'total_value',
          Cell: (props) => {
            if (
              props.row.original.sub_rows &&
              !props.row.original.sub_rows.length
            ) {
              return '-';
            }
            return (
              <>
                <div>
                  <Number value={props.value} format={true} />
                </div>
                <div className="invested">
                  <span className="text" title="Invested">
                    Inv
                  </span>
                  <Number value={props.row.original.total_cost} format={true} />
                </div>
              </>
            );
          },
          thClass: `pl-4 ${props.isBroker && 'icon-left '}`,
          tdClass: `table-data `,
          sortType: (rowA, rowB, id, desc) => {
            if (!rowA.depth) return false;
            let a = rowA.values[id];
            let b = rowB.values[id];
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
          },
        },
      ];
      if (props.showViewEditTx) {
        config.push({
          Header: '',
          accessor: 'view_tx',
          Cell: (props) => {
            return props.row.canExpand ||
              props.row.original.sub_rows ||
              (props.isBroker &&
                props.row.original.instrumentType != 'mutual_funds') ? (
              ''
            ) : (
              <>
                <span
                  className="edit-pill"
                  onClick={(e) => props.onThreedotsClick(e, props.row.original)}
                >
                  <ThreeDots color="#BFBFBF" />
                </span>
              </>
            );
          },
          thClass: 'pl-5',
          tdClass: 'table-data pr-3',
          disableSortBy: true,
        });
      }
      return config;
    }
    return [];
  }, [props.summary, props.returnsType]);

  const data = useMemo(() => {
    if (props.summary.active) {
      return props.summary.active;
    }
    return [];
  }, [props.summary]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,
  } = useTable(
    {
      columns,
      data: updatedData,
      getSubRows: (row) => row.sub_rows,
    },
    useSortBy,
    useExpanded,
  );

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (
      column.isSortedDesc ? (
        <FontAwesomeIcon icon={faCaretDown} className="sort-icon" />
      ) : (
        <FontAwesomeIcon icon={faCaretUp} className="sort-icon" />
      )
    ) : (
      <FontAwesomeIcon icon={faSort} className="sort-icon" />
    );
  };

  return (
    <table id="active-pos" className="mob data-table mt-0">
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <th
                {...column.getHeaderProps([
                  {
                    className: column.thClass,
                  },
                  column.getSortByToggleProps(),
                ])}
                onClick={(e) => {
                  if (!column.disableSortBy) {
                    column.getSortByToggleProps().onClick(e);
                    if (rows.some((r) => r.isExpanded)) return;
                    toggleAllRowsExpanded(true);
                  }
                }}
              >
                {column.render('Header')}
                {!column.disableSortBy && generateSortingIndicator(column)}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()} className="table-row">
              {row.cells.map((cell) => {
                return (
                  <td
                    {...cell.getCellProps({
                      className: cell.column.tdClass,
                    })}
                    className={`${cell.column.tdClass} ${
                      ['one_day_change', 'gain_loss'].includes(cell.column.id)
                        ? get_data_class(cell.value)
                        : ''
                    }`}
                  >
                    {cell.render('Cell', { ...props })}
                  </td>
                );
              })}
            </tr>
          );
        })}
        {props.summary && props.summary.total && (
          <tr className="total-row">
            <td>TOTAL</td>
            <td
              className={
                get_data_class(props.summary.total.one_day_change) +
                ' text-right'
              }
            >
              <div className="align-middle">
                <Number
                  value={props.summary.total.one_day_change}
                  format={true}
                />
              </div>
              <span className="pill">
                {props.summary.total.one_day_change_perc}%
              </span>
            </td>
            <td
              className={
                get_data_class(props.summary.total.gain_loss) + ` text-right`
              }
            >
              <div className="align-middle">
                <Number value={props.summary.total.gain_loss} format={true} />{' '}
              </div>
              <span className="pill">
                {props.returnsType === 'xirr'
                  ? props.summary.total.gain_loss_perc
                  : props.summary.total.gain_loss_abs_perc}
                %
              </span>
            </td>
            <td className="text-right">
              <div>
                <Number
                  value={props.summary.total.market_value}
                  format={true}
                />
              </div>
              <div className="invested">
                <span className="text" title="Invested">
                  Inv
                </span>
                <Number value={props.summary.total.cost_active} format={true} />
              </div>
            </td>
            {props.showViewEditTx && <td></td>}
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default ActivePosTableMob;
